import React, { useState } from 'react'
import { Box, Typography, Alert, CircularProgress } from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import Parse from 'parse'
import authService from '../../services/authService'
import { StyledButton } from '../CompanyManagement/styles/StyledComponents'
import {
  clearGoogleOAuthSignupConsent,
  writeGoogleOAuthHandoffToLocalStorage,
} from '../../utils/googleOAuthClient'

export const GOOGLE_OAUTH_INTENT_LINK = 'link'

export interface ConnectedAccountsPanelProps {
  email: string
  googleLinked: boolean
  onGoogleLinkChanged: (linked: boolean) => void
}

/**
 * Google connect / disconnect for an already signed-in user (email must match Google account).
 */
const ConnectedAccountsPanel: React.FC<ConnectedAccountsPanelProps> = ({
  email,
  googleLinked,
  onGoogleLinkChanged,
}) => {
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const startLinkGoogle = async () => {
    setLocalError(null)
    setBusy(true)
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`
      const { url, state } = await authService.getGoogleOAuthURL(redirectUri)
      if (state) {
        sessionStorage.setItem('google_oauth_state', state)
      }
      sessionStorage.setItem('google_oauth_redirect_uri', redirectUri)
      sessionStorage.setItem('google_oauth_intent', GOOGLE_OAUTH_INTENT_LINK)
      writeGoogleOAuthHandoffToLocalStorage(state, redirectUri)
      // Linking never creates an account; a sign-up choice left by an abandoned Google
      // sign-up must not travel with this flow. [SC-360]
      clearGoogleOAuthSignupConsent()
      window.location.href = url
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not start Google linking'
      setLocalError(msg)
      setBusy(false)
    }
  }

  const unlinkGoogle = async () => {
    setLocalError(null)
    setBusy(true)
    try {
      await authService.unlinkGoogleAccountFromCurrentUser()
      const cu = Parse.User.current()
      if (cu) {
        await cu.fetch()
      }
      onGoogleLinkChanged(false)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not unlink Google'
      setLocalError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Link Google to sign in faster and use your Google profile photo. Your Google email must match{' '}
        <strong>{email || 'your account email'}</strong>.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GoogleIcon color="action" />
            <Typography fontWeight={600}>Google</Typography>
            <Typography variant="caption" color="text.secondary">
              {googleLinked ? 'Linked' : 'Not linked'}
            </Typography>
          </Box>
          {googleLinked ? (
            <StyledButton
              variant="outlined"
              color="secondary"
              disabled={busy}
              onClick={() => void unlinkGoogle()}
            >
              {busy ? <CircularProgress size={18} /> : 'Unlink'}
            </StyledButton>
          ) : (
            <StyledButton
              variant="contained"
              disabled={busy}
              startIcon={<GoogleIcon />}
              onClick={() => void startLinkGoogle()}
            >
              {busy ? <CircularProgress size={18} color="inherit" /> : 'Link Google'}
            </StyledButton>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ConnectedAccountsPanel
